/**
 * Scenery streamed per chunk (lane C0-P1, docs/design/C0_TOWN_LAYOUT.md §7): on a big map the trees, rocks,
 * buildings and ground cover are made for the chunks near the camera only, as the ground's chunks are painted,
 * instead of for the whole map at once (≈ 2.5 s and all its memory on the 256 m plains). Chunks in view are made
 * under the frame's art budget (drawBudget.ts), those just past the view's edge in idle time, and the farthest are
 * dropped past `KEEP`.
 *
 * It is a `PropGrid` to the renderer and the passes (`props`, `query`, `width`, `height`): `props` is the scenery of
 * the chunks held now (a new array whenever that changes, so the passes that cache by it see the change), and
 * `query` gives indices into it.
 */
import type { GameMap, IsoOrigin, Structure } from '@nine/shared';
import { structureLook } from './layoutLook';
import { toScreen } from '@nine/shared';
import { canDraw, later, timed } from './drawBudget';
import { gpuPrepare } from './gpuPrepare';
import { PropGrid } from './propGrid';
import { artH, artW } from './pixel';
import { buildingLook, buildingStrips, finishProps, propKit, propsIn, type Prop, type PropKit, type TileRect, type ViewMapping } from './terrain';

/** Chunk side in tiles. */
const SIDE = 16;
/** Chunks held at most (about four screens' worth round the camera). */
const KEEP = 72;
/** How far past the view's edge (world pixels) chunks are made ahead, in idle time. */
const AHEAD = 320;
/** Room a chunk's scenery may take past its tiles (world pixels): trees and rocks up, crowns to the sides. */
const UP = 220;
const SIDEWAYS = 64;
/** Room for a building past its footprint: its roof up. */
const ROOF_UP = 360;

/** Whether a map's scenery is streamed (the big maps; the 72 m plains keep the whole-map build). */
export const streamScenery = (m: GameMap): boolean => m.w * m.h > 128 * 128 && !noStream;
/** Measuring only: `localStorage['c0.streamScenery'] = '0'` builds a big map's scenery whole, as before streaming. */
const noStream = (() => {
  try {
    return localStorage.getItem('c0.streamScenery') === '0';
  } catch {
    return false;
  }
})();

interface Chunk {
  readonly area: TileRect;
  readonly structures: Structure[];
  /** Where its scenery may reach (world pixels), tightened to the real extent once made. */
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  props: Prop[] | null;
  w: Float32Array | null;
  h: Float32Array | null;
  /** Asked for in idle time and not made yet. */
  queued: boolean;
  /** The last query that saw it (for dropping the least recent). */
  seen: number;
}

export class StreamedProps extends PropGrid {
  private readonly kit: PropKit;
  private readonly chunks: Chunk[] = [];
  private readonly n: number;
  /** The held chunks' scenery, flat; rebuilt when a chunk is made or dropped. */
  private fw = new Float32Array(0);
  private fh = new Float32Array(0);
  private readonly base = new Map<Chunk, number>();
  private readonly hits: number[] = [];
  private queries = 0;
  /** Chunks made, and the time they took (ms), for the perf readout. */
  readonly stats = { made: 0, ms: 0, maxMs: 0, dropped: 0, held: 0 };

  constructor(
    private readonly m: GameMap,
    private readonly o: IsoOrigin,
    private readonly view: ViewMapping,
    /** Scenery of the passes (training spots), small: held all the time, as one more chunk. */
    extra: readonly Prop[],
  ) {
    super([]);
    this.kit = propKit(m, true);
    // Every building's picture now (the loading screen; kept across camera angles): the slow part of a chunk,
    // which would otherwise stall the frame that first sees it.
    for (const raw of m.structures) {
      const st = structureLook(raw);
      if (!st) continue;
      // Onto the GPU too, between frames: the hall's and the inn's first sight cost a frame (83 ms) otherwise.
      for (const strip of buildingStrips(this.kit, buildingLook(this.kit, st))) gpuPrepare.fn?.(strip);
    }
    this.n = Math.ceil(m.w / SIDE);
    const n = this.n;
    for (let cj = 0; cj < n; cj++)
      for (let ci = 0; ci < n; ci++) {
        const i0 = ci * SIDE;
        const j0 = cj * SIDE;
        const i1 = Math.min(m.w, i0 + SIDE);
        const j1 = Math.min(m.h, j0 + SIDE);
        // The tiles' diamond on screen: west corner (i0, j1), east (i1, j0), north (i0, j0), south (i1, j1).
        const [xw] = toScreen(i0, j1, o);
        const [xe] = toScreen(i1, j0, o);
        const [, yn] = toScreen(i0, j0, o);
        const [, ys] = toScreen(i1, j1, o);
        this.chunks.push({
          area: [i0, i1, j0, j1],
          structures: [],
          x0: xw - SIDEWAYS,
          x1: xe + SIDEWAYS,
          y0: yn - UP,
          y1: ys + 24,
          props: null,
          w: null,
          h: null,
          queued: false,
          seen: 0,
        });
      }
    // Each building goes with the chunk under its footprint's middle; that chunk's reach grows to hold it.
    for (const st of m.structures) {
      const ci = Math.min(n - 1, Math.max(0, Math.floor((st.i + st.w / 2) / SIDE)));
      const cj = Math.min(n - 1, Math.max(0, Math.floor((st.j + st.h / 2) / SIDE)));
      const c = this.chunks[cj * n + ci]!;
      c.structures.push(st);
      const [xw] = toScreen(st.i, st.j + st.h, o);
      const [xe] = toScreen(st.i + st.w, st.j, o);
      const [, yn] = toScreen(st.i, st.j, o);
      const [, ys] = toScreen(st.i + st.w, st.j + st.h, o);
      c.x0 = Math.min(c.x0, xw - SIDEWAYS);
      c.x1 = Math.max(c.x1, xe + SIDEWAYS);
      c.y0 = Math.min(c.y0, yn - ROOF_UP);
      c.y1 = Math.max(c.y1, ys + 24);
    }
    // The passes' scenery: one more chunk, made at once and never dropped.
    if (extra.length) {
      const c: Chunk = { area: [0, 0, 0, 0], structures: [], x0: 0, x1: 0, y0: 0, y1: 0, props: null, w: null, h: null, queued: false, seen: Infinity };
      this.chunks.push(c);
      this.fill(c, [...extra]);
    }
  }

  override width(i: number): number {
    return this.fw[i]!;
  }
  override height(i: number): number {
    return this.fh[i]!;
  }

  /**
   * Indices of the scenery overlapping the rectangle (world pixels), chunk by chunk. Chunks in it that are not made
   * yet are made now while the frame's art budget lasts (the rest on the next frames); chunks just past it are
   * asked for in idle time. The array is reused by the next query.
   */
  override query(x0: number, x1: number, y0: number, y1: number): readonly number[] {
    const q = ++this.queries;
    const want: Chunk[] = [];
    for (const c of this.chunks) {
      if (c.x1 < x0 - AHEAD || c.x0 > x1 + AHEAD || c.y1 < y0 - AHEAD || c.y0 > y1 + AHEAD) continue;
      const inView = !(c.x1 < x0 || c.x0 > x1 || c.y1 < y0 || c.y0 > y1);
      if (inView) c.seen = q;
      if (c.props) {
        if (!inView) c.seen = Math.max(c.seen, q - 1);
        continue;
      }
      if (inView) want.push(c);
      else this.ask(c);
    }
    // In view: the one nearest the middle first.
    if (want.length) {
      const mx = (x0 + x1) / 2;
      const my = (y0 + y1) / 2;
      want.sort((a, b) => Math.hypot((a.x0 + a.x1) / 2 - mx, (a.y0 + a.y1) / 2 - my) - Math.hypot((b.x0 + b.x1) / 2 - mx, (b.y0 + b.y1) / 2 - my));
      for (const c of want) {
        if (!canDraw()) {
          this.ask(c, true);
          continue;
        }
        timed(() => this.make(c));
      }
    }
    this.drop(q);
    const found = this.hits;
    found.length = 0;
    for (const c of this.chunks) {
      if (!c.props || c.x1 < x0 || c.x0 > x1 || c.y1 < y0 || c.y0 > y1) continue;
      const b = this.base.get(c)!;
      const { props, w, h } = c;
      for (let k = 0; k < props.length; k++) {
        const pr = props[k]!;
        if (pr.x + w![k]! < x0 || pr.x > x1 || pr.y + h![k]! < y0 || pr.y > y1) continue;
        found.push(b + k);
      }
    }
    return found;
  }

  /** Makes every chunk overlapping the rectangle now (the loading screen, a teleport's first frame). */
  warm(x0: number, x1: number, y0: number, y1: number): void {
    for (const c of this.chunks)
      if (!c.props && !(c.x1 < x0 || c.x0 > x1 || c.y1 < y0 || c.y0 > y1)) this.make(c);
  }

  private ask(c: Chunk, first = false): void {
    if (c.queued) return;
    c.queued = true;
    later(() => {
      c.queued = false;
      if (!c.props) {
        c.seen = this.queries;
        this.make(c);
        // Made ahead of the view: its pictures go to the GPU before it is seen.
        for (const p of (c.props as Prop[] | null) ?? []) gpuPrepare.fn?.(p.img);
      }
    }, first);
  }

  private make(c: Chunk): void {
    if (c.props) return;
    const t0 = performance.now();
    const own = finishProps(this.kit, propsIn(this.kit, this.m, this.o, this.view, c.area, c.structures, (x, y) => this.has(c, x, y)));
    this.fill(c, own);
    const ms = performance.now() - t0;
    this.stats.made++;
    this.stats.ms += ms;
    this.stats.maxMs = Math.max(this.stats.maxMs, ms);
  }

  /** Whether a map point (view tiles) falls in the chunk's tiles: the plaza's lanterns and swords. */
  private has(c: Chunk, x: number, y: number): boolean {
    const [i0, i1, j0, j1] = c.area;
    return x >= i0 && x < i1 && y >= j0 && y < j1;
  }

  private fill(c: Chunk, props: Prop[]): void {
    c.props = props;
    c.w = new Float32Array(props.length);
    c.h = new Float32Array(props.length);
    if (props.length) {
      let [x0, x1, y0, y1] = [Infinity, -Infinity, Infinity, -Infinity];
      props.forEach((pr, k) => {
        const w = artW(pr.img);
        const h = artH(pr.img);
        c.w![k] = w;
        c.h![k] = h;
        x0 = Math.min(x0, pr.x);
        x1 = Math.max(x1, pr.x + w);
        y0 = Math.min(y0, pr.y);
        y1 = Math.max(y1, pr.y + h);
      });
      Object.assign(c, { x0, x1, y0, y1 });
    }
    this.flatten();
  }

  /** Drops the least recently seen chunks past `KEEP` (never one in view). */
  private drop(q: number): void {
    const held = this.chunks.filter((c) => c.props && c.seen !== Infinity);
    this.stats.held = held.length;
    if (held.length <= KEEP) return;
    held.sort((a, b) => a.seen - b.seen);
    let dropped = false;
    for (const c of held.slice(0, held.length - KEEP)) {
      if (c.seen >= q) break;
      c.props = c.w = c.h = null;
      this.stats.dropped++;
      dropped = true;
    }
    if (dropped) this.flatten();
  }

  private flatten(): void {
    this.base.clear();
    const flat: Prop[] = [];
    for (const c of this.chunks) {
      if (!c.props) continue;
      this.base.set(c, flat.length);
      for (const p of c.props) flat.push(p);
    }
    const fw = new Float32Array(flat.length);
    const fh = new Float32Array(flat.length);
    for (const c of this.chunks) {
      if (!c.props) continue;
      const b = this.base.get(c)!;
      fw.set(c.w!, b);
      fh.set(c.h!, b);
    }
    // `props` is the grid's own field: set as a whole new array, so caches keyed by it see the change.
    (this as unknown as { props: readonly Prop[] }).props = flat;
    this.fw = fw;
    this.fh = fh;
  }
}
